/**
 * Pulse Voice — client-side structured types + framing catalog.
 *
 * Frontend mirror of the canonical definitions in
 * `supabase/functions/_shared/voice/{types,catalog}.ts`. Kept in sync manually
 * because Deno and Vitest use different module resolution (same rationale as the
 * `AutomationSettings` mirror in `use-email-accounts.ts`).
 *
 * Deliberately omits the framing `directive` prose: directives are only ever
 * rendered into the compiled, hidden tone prompt server-side. The client edits
 * the structured `VoiceProfile` and never sees prompt text — keeping that
 * boundary structural rather than policy-dependent.
 *
 * See docs/plans/2026-06-16-feat-pulse-voice-onboarding-plan.md.
 */

import {
  FORMALITY_OPTIONS,
  LENGTH_OPTIONS,
  WARMTH_OPTIONS,
  type AxisOption,
  type Formality,
  type Length,
  type VoiceContactMode,
  type VoiceSignOff,
  type Warmth,
} from './voice-axes';
import { parseEvidenceValue, type VoiceEvidence, type VoiceEvidenceQuote } from './voice-evidence';

export type { AxisOption };
export {
  axisExample,
  FORMALITY_OPTIONS,
  LENGTH_OPTIONS,
  SIGN_OFF_MODE_OPTIONS,
  WARMTH_OPTIONS,
} from './voice-axes';
export type { VoiceEvidence, VoiceEvidenceQuote };
export type { Formality, Length, VoiceContactMode, VoiceSignOff, Warmth } from './voice-axes';

/** Key into the fixed framing catalog. Mirrors `_shared/voice/types.ts`. */
export type FramingId =
  | 'warm_personal'
  | 'formal_institutional'
  | 'direct_efficient'
  | 'friendly_professional'
  | 'empathetic_advocate';

export interface VoiceAxes {
  formality: Formality;
  length: Length;
  warmth: Warmth;
  contractions: boolean;
}

export interface VoiceIdentity {
  /** Sign-off name. */
  displayName?: string;
  /** e.g. "Electorate Officer", "Practice Manager". */
  title?: string;
  /** Person/office they represent. */
  worksFor?: string;
}

/** How a reply opens. Mirrors {@link VoiceSignOff}. */
export interface VoiceGreeting {
  mode: VoiceContactMode;
  /** Only meaningful when `mode === 'custom'`. */
  text?: string;
}

/** Provenance for "re-analyse" + audit. Never rendered into the prompt. */
export interface VoiceProfileSource {
  analysedAt?: string;
  sampleCount?: number;
  coldStart?: boolean;
}

export interface VoiceProfile {
  version: 1;
  identity: VoiceIdentity;
  framingId: FramingId;
  axes: VoiceAxes;
  greeting: VoiceGreeting;
  signOff: VoiceSignOff;
  source?: VoiceProfileSource;
  /**
   * Free-text role for this send-as address — who Pulse writes as. Per-account,
   * appended to the draft prompt's role block (org role is the fallback). See
   * `_shared/voice/types.ts`.
   */
  roleDescription?: string;
  /** Anonymised prose describing how this writer writes. See `_shared/voice/types.ts`. */
  fingerprint?: string;
  /** Anonymised few-shot example replies (greeting included, sign-off excluded). */
  examples?: string[];
  /** Persisted per-axis "in your words" evidence quotes. See `_shared/voice/types.ts`. */
  evidence?: VoiceEvidence;
}

/** One framing surfaced to the user by the analyser, best fit first. */
export interface ProposedFraming {
  id: FramingId;
  label: string;
  description: string;
}

/** An observed greeting / sign-off: the most common value + alternatives. */
export interface VoiceObservedField {
  value: string;
  alternatives: string[];
}

/** The provider signature surfaced for the user to affirm. */
export interface VoiceSignatureInfo {
  text: string;
  detected: boolean;
}

/** Response shape of the `analyze-voice` edge function. */
export interface AnalyzeVoiceResult {
  /** Proposed, NOT persisted. The user confirms via `set-voice-profile`. */
  proposedProfile: VoiceProfile;
  /** Candidate framings to show, best fit first. */
  framings: ProposedFraming[];
  /** Model confidence in the proposal, 0..1. */
  confidence: number;
  /** True when no usable sent mail was available (empty/unsupported mailbox). */
  coldStart: boolean;
  /** Number of sent samples actually analysed. */
  sampleCount: number;
  // Display-only enrichments (absent on cold start). Never persisted.
  /** Inferred role/identity description (feeds org_profile later). */
  role?: string;
  /**
   * Short adjective summary from the analyser, e.g. "Formal, considered and
   * diplomatic". Part of the analyser response contract but no longer surfaced in
   * the calibration UI (the header leads with name + role instead).
   */
  summaryHeadline?: string;
  /** Verbatim example quotes per tone axis. */
  evidence?: {
    formality: VoiceEvidenceQuote[];
    warmth: VoiceEvidenceQuote[];
    length: VoiceEvidenceQuote[];
  };
  greeting?: VoiceObservedField;
  signOff?: VoiceObservedField;
  /** Provider signature for the user to affirm. */
  signature?: VoiceSignatureInfo;
}

/** UI-facing framing entry (label + description + centred axes — no directive prose). */
export interface VoiceFramingOption {
  id: FramingId;
  label: string;
  description: string;
  /** Axis values this framing is centred on, mirrored from the server catalog. */
  defaultAxes: VoiceAxes;
}

/**
 * The fallback framing when a profile's `framingId` is unknown — mirrors
 * `_shared/voice/catalog.ts` so the client renders the same default the
 * compiler resolves.
 */
export const DEFAULT_FRAMING_ID: FramingId = 'friendly_professional';

/**
 * Client framing catalog. Labels + descriptions are copied verbatim from
 * `_shared/voice/catalog.ts`; if they drift, the server prose is canonical.
 */
export const VOICE_FRAMING_OPTIONS: readonly VoiceFramingOption[] = [
  {
    id: 'warm_personal',
    label: 'Warm & personal',
    description: 'Approachable and human — like a helpful person, not an institution.',
    defaultAxes: { formality: 'casual', length: 'balanced', warmth: 'warm', contractions: true },
  },
  {
    id: 'formal_institutional',
    label: 'Formal & institutional',
    description: 'Measured, precise, and authoritative — the voice of the office of record.',
    defaultAxes: { formality: 'formal', length: 'detailed', warmth: 'plain', contractions: false },
  },
  {
    id: 'direct_efficient',
    label: 'Direct & efficient',
    description: 'Brief and action-oriented — respects the reader’s time.',
    defaultAxes: { formality: 'balanced', length: 'concise', warmth: 'plain', contractions: true },
  },
  {
    id: 'friendly_professional',
    label: 'Friendly & professional',
    description: 'Polished but approachable — a safe, balanced default.',
    defaultAxes: { formality: 'balanced', length: 'balanced', warmth: 'warm', contractions: true },
  },
  {
    id: 'empathetic_advocate',
    label: 'Empathetic advocate',
    description: 'Leads with understanding and a clear commitment to help.',
    defaultAxes: { formality: 'balanced', length: 'detailed', warmth: 'warm', contractions: true },
  },
];

/** A blank, never-calibrated profile for the cold-start editing form. */
export function makeDefaultVoiceProfile(): VoiceProfile {
  return {
    version: 1,
    framingId: DEFAULT_FRAMING_ID,
    axes: { ...getFramingOption(DEFAULT_FRAMING_ID).defaultAxes },
    identity: {},
    greeting: { mode: 'auto' },
    signOff: { mode: 'auto' },
  };
}

const FRAMING_OPTION_BY_ID: ReadonlyMap<FramingId, VoiceFramingOption> = new Map(
  VOICE_FRAMING_OPTIONS.map((option) => [option.id, option]),
);

/** True when `id` is a known catalog key. */
export function isFramingId(id: unknown): id is FramingId {
  return typeof id === 'string' && FRAMING_OPTION_BY_ID.has(id as FramingId);
}

// The default framing is seeded into VOICE_FRAMING_OPTIONS, so this lookup is
// always present — resolved once here to anchor a structurally non-null return.
const DEFAULT_FRAMING_OPTION: VoiceFramingOption =
  FRAMING_OPTION_BY_ID.get(DEFAULT_FRAMING_ID) ?? VOICE_FRAMING_OPTIONS[0];

/**
 * Resolve a framing option by key, falling back to {@link DEFAULT_FRAMING_ID}
 * for an unknown key so the UI always has a framing to render.
 */
export function getFramingOption(id: string): VoiceFramingOption {
  return FRAMING_OPTION_BY_ID.get(id as FramingId) ?? DEFAULT_FRAMING_OPTION;
}

function oneOf<T extends string>(
  value: unknown,
  options: readonly AxisOption<T>[],
  fallback: T,
): T {
  return options.some((o) => o.value === value) ? (value as T) : fallback;
}

export function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/** Mirror of the backend `MAX_EXAMPLES` (`_shared/voice/parse.ts`) — keep in lockstep. */
const MAX_VOICE_EXAMPLES = 6;

/** Mirror of the backend `MAX_ORG_ROLE_LENGTH` (`_shared/voice/org-profile.ts`). */
const MAX_ROLE_LENGTH = 600;

/** Boundary guard for the few-shot examples list: strings only, empties dropped, capped. */
function parseExamples(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const out = value.map((item) => readString(item)).filter((v): v is string => v !== undefined);
  return out.length > 0 ? out.slice(0, MAX_VOICE_EXAMPLES) : undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function parseAxesValue(raw: unknown, defaults: VoiceAxes): VoiceAxes {
  const r = asRecord(raw);
  return {
    formality: oneOf(r.formality, FORMALITY_OPTIONS, defaults.formality),
    length: oneOf(r.length, LENGTH_OPTIONS, defaults.length),
    warmth: oneOf(r.warmth, WARMTH_OPTIONS, defaults.warmth),
    contractions: typeof r.contractions === 'boolean' ? r.contractions : defaults.contractions,
  };
}

function parseIdentityValue(raw: unknown): VoiceIdentity {
  const r = asRecord(raw);
  return {
    displayName: readString(r.displayName),
    title: readString(r.title),
    worksFor: readString(r.worksFor),
  };
}

const CONTACT_MODES: ReadonlySet<VoiceContactMode> = new Set(['none', 'auto', 'custom']);

/** Parse a greeting/sign-off block: a bounded mode + custom-only text. */
function parseModeText(raw: unknown): { mode: VoiceContactMode; text?: string } {
  const r = asRecord(raw);
  const mode: VoiceContactMode = CONTACT_MODES.has(r.mode as VoiceContactMode)
    ? (r.mode as VoiceContactMode)
    : 'auto';
  return { mode, text: mode === 'custom' ? readString(r.text) : undefined };
}

/** Validate the provenance block field-by-field (mirrors `_shared/voice/parse.ts`). */
function parseSource(raw: unknown): VoiceProfileSource | undefined {
  const r = asRecord(raw);
  const source: VoiceProfileSource = {
    analysedAt: readString(r.analysedAt),
    sampleCount:
      typeof r.sampleCount === 'number' && Number.isFinite(r.sampleCount)
        ? r.sampleCount
        : undefined,
    coldStart: typeof r.coldStart === 'boolean' ? r.coldStart : undefined,
  };
  return Object.values(source).some((v) => v !== undefined) ? source : undefined;
}

/**
 * Coerce the untyped `email_accounts.voice_profile` JSON into a typed
 * {@link VoiceProfile} for the settings controls. The `set-voice-profile` edge
 * function is the sole writer and validates on write, so a stored profile is
 * trustworthy — this is a boundary guard, not a second validation layer. Returns
 * `null` for an absent (un-calibrated) or non-object value.
 */
export function parseVoiceProfile(raw?: unknown): VoiceProfile | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const framingId = isFramingId(r.framingId) ? r.framingId : DEFAULT_FRAMING_ID;
  const framing = getFramingOption(framingId);
  const profile: VoiceProfile = {
    version: 1,
    framingId,
    axes: parseAxesValue(r.axes, framing.defaultAxes),
    identity: parseIdentityValue(r.identity),
    greeting: parseModeText(r.greeting),
    signOff: parseModeText(r.signOff),
    source: parseSource(r.source),
  };
  const roleDescription = readString(r.roleDescription);
  if (roleDescription) {
    profile.roleDescription = roleDescription.slice(0, MAX_ROLE_LENGTH).trim();
  }
  const fingerprint = readString(r.fingerprint);
  if (fingerprint) {
    profile.fingerprint = fingerprint;
  }
  const examples = parseExamples(r.examples);
  if (examples) {
    profile.examples = examples;
  }
  const evidence = parseEvidenceValue(r.evidence);
  if (evidence) {
    profile.evidence = evidence;
  }
  return profile;
}
